import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useNearContext } from '../contexts/NearContext.jsx';
import { useWager } from '../hooks/useWagers.js';
import LoadingSpinner from './LoadingSpinner.jsx';

const WagerDetail = () => {
  const { id } = useParams();
  const numericId = useMemo(() => (id ? parseInt(id, 10) : null), [id]);

  const {
    contractApi,
    nearService,
    accountId,
    isSignedIn,
    executeTransaction,
    formatNearAmount,
  } = useNearContext();

  const {
    data: wager,
    isLoading,
    isError,
    refetch,
  } = useWager(numericId, { enabled: numericId != null });

  const [selectedChoice, setSelectedChoice] = useState('');
  const [stakeAmount, setStakeAmount] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [resolveOutcome, setResolveOutcome] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);

  const toNearNumber = useCallback(
    (amount) => {
      if (!amount) {
        return 0;
      }
      try {
        const formatted = nearService?.formatNearAmount(amount) ?? '0';
        const normalized = formatted.replace(/,/g, '');
        const parsed = Number.parseFloat(normalized);
        return Number.isNaN(parsed) ? 0 : parsed;
      } catch (error) {
        console.warn('Failed to parse NEAR amount:', error);
        return 0;
      }
    },
    [nearService]
  );

  const participants = useMemo(
    () => (Array.isArray(wager?.participants) ? wager.participants : []),
    [wager]
  );

  const isParticipant = useMemo(
    () => participants.some((participant) => participant?.account_id === accountId),
    [participants, accountId]
  );

  const userParticipant = useMemo(
    () => participants.find((participant) => participant?.account_id === accountId),
    [participants, accountId]
  );

  const isCreator = wager?.creator === accountId;

  const canJoin = Boolean(
    wager &&
      (wager.status === 'Open' || wager.status === 'Active') &&
      isSignedIn &&
      !isParticipant
  );

  const handleCopyLink = useCallback(async () => {
    const shareUrl = window?.location?.href ?? '';

    if (!shareUrl) {
      toast.error('Link not available yet. Please try again.');
      return;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ url: shareUrl, title: wager?.request?.title ?? 'NearBets wager' });
        return;
      }

      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopiedLink(true);
        toast.success('Wager link copied to clipboard');
        setTimeout(() => setCopiedLink(false), 2500);
        return;
      }

      toast('Copy the link directly from your browser address bar.');
    } catch (error) {
      console.error('Failed to copy wager link:', error);
      toast.error('Unable to copy link. Try copying from the browser address bar.');
    }
  }, [wager]);

  const minStakeYocto = wager?.request?.min_stake || '0';
  const minStakeNumeric = toNearNumber(minStakeYocto);
  const minStakeDisplay = formatNearAmount(minStakeYocto);

  const availableChoices = useMemo(
    () => (Array.isArray(wager?.request?.choices) ? wager.request.choices : []),
    [wager]
  );

  const canResolve = Boolean(
    isCreator &&
    wager &&
    wager.status === 'Active' &&
    availableChoices.length > 0
  );

  const userWinnings = userParticipant?.winnings ? toNearNumber(userParticipant.winnings) : 0;
  const hasPendingClaim = Boolean(
    wager &&
    wager.status === 'Resolved' &&
    userParticipant &&
    userWinnings > 0 &&
    !userParticipant.claimed_winnings
  );
  const userStakeDisplay = formatNearAmount(userParticipant?.stake || '0');
  const userWinningsDisplay = formatNearAmount(userParticipant?.winnings || '0');
  const hasClaimed = Boolean(userParticipant?.claimed_winnings);

  const handleJoin = async () => {
    if (!contractApi || numericId == null || Number.isNaN(numericId)) {
      toast.error('Contract not ready — please refresh the page.');
      return;
    }

    if (!selectedChoice) {
      toast.error('Choose a side before joining.');
      return;
    }

    const numericStake = Number.parseFloat(stakeAmount);

    if (!stakeAmount || Number.isNaN(numericStake) || numericStake <= 0) {
      toast.error('Enter the amount of NEAR you want to stake.');
      return;
    }

    if (numericStake < minStakeNumeric) {
      toast.error(`Stake must be at least ${minStakeDisplay} NEAR.`);
      return;
    }

    if (!isSignedIn) {
      toast.error('Please connect your wallet first.');
      return;
    }

    setIsJoining(true);
    try {
      await executeTransaction(
        () => contractApi.joinWager(numericId, selectedChoice, stakeAmount),
        `Joined wager with ${stakeAmount} NEAR`
      );
      await refetch();
      setSelectedChoice('');
      setStakeAmount('');
    } catch (error) {
      console.error('Failed to join wager:', error);
      toast.error(error.message || 'Failed to join wager');
    } finally {
      setIsJoining(false);
    }
  };

  const handleResolve = async () => {
    if (!contractApi || numericId == null || Number.isNaN(numericId)) {
      toast.error('Contract not ready — please refresh the page.');
      return;
    }

    if (!isCreator) {
      toast.error('Only the wager creator can resolve the outcome.');
      return;
    }

    if (!resolveOutcome) {
      toast.error('Select the winning outcome before resolving.');
      return;
    }

    setIsResolving(true);
    try {
      await executeTransaction(
        () => contractApi.resolveWager(numericId, resolveOutcome),
        'Wager resolved successfully!'
      );
      setResolveOutcome('');
      await refetch();
    } catch (error) {
      console.error('Failed to resolve wager:', error);
      toast.error(error.message || 'Failed to resolve wager');
    } finally {
      setIsResolving(false);
    }
  };

  const handleClaim = async () => {
    if (!contractApi || numericId == null || Number.isNaN(numericId)) {
      toast.error('Contract not ready — please refresh the page.');
      return;
    }

    setIsClaiming(true);
    try {
      await executeTransaction(
        () => contractApi.claimWinnings(numericId),
        'Winnings claimed!'
      );
      await refetch();
    } catch (error) {
      console.error('Failed to claim winnings:', error);
      toast.error(error.message || 'Failed to claim winnings');
    } finally {
      setIsClaiming(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card max-w-xl mx-auto text-center text-red-500">
        We couldn&apos;t load this wager. It may have been removed or the network is busy.
      </div>
    );
  }

  if (!wager) {
    return <div className="text-center text-gray-500">Wager not found</div>;
  }

  const totalPool = formatNearAmount(wager.total_stake || '0');
  const createdAt = wager.created_at
    ? new Date(Number(wager.created_at) / 1_000_000).toLocaleString()
    : 'Unknown';
  const resolvedAt = wager.resolved_at
    ? new Date(Number(wager.resolved_at) / 1_000_000).toLocaleString()
    : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="card space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gradient">{wager.request?.title}</h1>
            <p className="text-gray-600 mt-2">{wager.request?.description}</p>
            {availableChoices.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {availableChoices.map((choice) => (
                  <span
                    key={`pill-${choice}`}
                    className="px-3 py-1 text-xs font-semibold uppercase tracking-wide bg-gray-100 text-gray-600 rounded-full"
                  >
                    {choice}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
              {wager.status}
            </span>
            <button
              type="button"
              onClick={handleCopyLink}
              className="btn-secondary text-xs"
              disabled={copiedLink}
            >
              {copiedLink ? 'Link copied' : 'Copy share link'}
            </button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
            <div className="text-sm text-purple-700">Total pool</div>
            <div className="text-2xl font-semibold text-purple-900">{totalPool} NEAR</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-sm text-blue-700">Participants</div>
            <div className="text-2xl font-semibold text-blue-900">{participants.length}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-sm text-green-700">Minimum stake</div>
            <div className="text-2xl font-semibold text-green-900">{minStakeDisplay} NEAR</div>
          </div>
        </div>
      </div>

      {canJoin && (
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Join this wager</h2>
            <button type="button" className="btn-secondary text-sm" onClick={refetch}>
              Refresh odds
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-2">Choose your side</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {availableChoices.map((choice) => (
                  <button
                    key={choice}
                    type="button"
                    className={`p-3 border rounded-lg transition ${
                      selectedChoice === choice
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={() => setSelectedChoice(choice)}
                  >
                    {choice}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-600" htmlFor="stakeAmount">
                Stake amount (NEAR)
              </label>
              <input
                id="stakeAmount"
                type="number"
                min={minStakeNumeric || 0.1}
                step="0.01"
                value={stakeAmount}
                onChange={(event) => setStakeAmount(event.target.value)}
                className="input-field"
                placeholder={`Minimum ${minStakeDisplay} NEAR`}
              />
            </div>

            <button
              type="button"
              className="btn-primary w-full"
              onClick={handleJoin}
              disabled={isJoining || !selectedChoice || !stakeAmount}
            >
              {isJoining ? 'Confirming transaction…' : `Join with ${stakeAmount || '0'} NEAR`}
            </button>
          </div>
        </div>
      )}

      {canResolve && (
        <div className="card space-y-4 border border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-amber-800">Resolve outcome</h2>
            <button type="button" className="btn-secondary text-sm" onClick={refetch}>
              Refresh data
            </button>
          </div>
          <p className="text-sm text-amber-700">
            Pick the winning selection once the result is final. Payouts and platform fees will distribute automatically.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {availableChoices.map((choice) => (
              <button
                key={`resolve-${choice}`}
                type="button"
                className={`p-3 border rounded-lg transition ${
                  resolveOutcome === choice
                    ? 'border-amber-500 bg-amber-100 text-amber-800'
                    : 'border-amber-200 hover:border-amber-400 text-amber-700'
                }`}
                onClick={() => setResolveOutcome(choice)}
              >
                {choice}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn-primary w-full"
            onClick={handleResolve}
            disabled={isResolving || !resolveOutcome}
          >
            {isResolving ? 'Submitting outcome…' : 'Confirm winning outcome'}
          </button>
        </div>
      )}

      {userParticipant && (
        <div className="card border border-green-200 bg-green-50 space-y-3">
          <h2 className="text-xl font-semibold text-green-800">Your position</h2>
          <div className="flex flex-wrap items-center justify-between gap-2 text-green-700">
            <span>
              Choice: <strong>{userParticipant.choice}</strong>
            </span>
            <span>
              Stake: {userStakeDisplay} NEAR
            </span>
          </div>
          {wager.status === 'Resolved' && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-green-700">
              <span>
                Winnings: <strong>{userWinningsDisplay} NEAR</strong>
              </span>
              <span>{hasClaimed ? 'Claimed' : 'Unclaimed'}</span>
            </div>
          )}

          {hasPendingClaim && (
            <button
              type="button"
              className="btn-primary w-full"
              onClick={handleClaim}
              disabled={isClaiming}
            >
              {isClaiming ? 'Claiming…' : `Claim ${userWinningsDisplay} NEAR`}
            </button>
          )}
        </div>
      )}

      <div className="card space-y-3">
        <h2 className="text-xl font-semibold">Participants</h2>
        {participants.length === 0 && <p className="text-sm text-gray-500">No one has joined yet.</p>}
        <div className="space-y-2">
          {participants.map((participant) => {
            const stakeDisplay = formatNearAmount(participant.stake || '0');
            const winningsValue = toNearNumber(participant.winnings || '0');
            const winningsDisplay = formatNearAmount(participant.winnings || '0');
            const claimStatus = winningsValue > 0
              ? participant.claimed_winnings
                ? 'Claimed'
                : 'Pending'
              : '—';

            return (
              <div
                key={`${participant.account_id}-${participant.choice}`}
                className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 border border-gray-100 rounded-lg p-3"
              >
                <div>
                  <p className="font-semibold text-gray-700">{participant.account_id}</p>
                  <p className="text-sm text-gray-500">Choice: {participant.choice}</p>
                </div>
                <div className="text-right text-sm text-gray-600">
                  <div className="font-semibold text-purple-700">Stake: {stakeDisplay} NEAR</div>
                  {wager.status === 'Resolved' && (
                    <div className="text-xs text-gray-500">
                      Winnings: {winningsDisplay} NEAR • {claimStatus}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card space-y-4">
        <h2 className="text-xl font-semibold">Wager details</h2>
        <dl className="grid gap-4 sm:grid-cols-2 text-sm">
          <div>
            <dt className="uppercase text-gray-500">Created</dt>
            <dd className="text-gray-800 font-medium">{createdAt}</dd>
          </div>
          <div>
            <dt className="uppercase text-gray-500">Creator</dt>
            <dd className="text-gray-800 font-medium">{wager.creator}</dd>
          </div>
          <div>
            <dt className="uppercase text-gray-500">Outcome</dt>
            <dd className="text-gray-800 font-medium">{wager.final_outcome ?? 'Pending'}</dd>
          </div>
        </dl>
      </div>

      {wager.final_outcome && (
        <div className="card border border-blue-200 bg-blue-50">
          <h2 className="text-xl font-semibold text-blue-800">Resolution</h2>
          <p className="text-blue-700">Final outcome: {wager.final_outcome}</p>
          {resolvedAt && (
            <p className="text-blue-600 text-sm">Resolved on {resolvedAt}</p>
          )}
        </div>
      )}

      {/* Transaction Timeline */}
      <div className="card space-y-4 border-t-4 border-purple-500">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Transaction History
        </h2>
        <div className="space-y-3">
          {/* Wager Created */}
          <div className="flex items-start gap-3 pb-3 border-b border-gray-200">
            <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-700 font-bold">1</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Wager Created</h3>
              <p className="text-sm text-gray-600">by {wager.creator}</p>
              <p className="text-xs text-gray-500 mt-1">{createdAt}</p>
              <a
                href={`https://nearblocks.io/address/${wager.creator}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 mt-1"
              >
                View creator on explorer
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>

          {/* Participants Joined */}
          {participants.map((participant, index) => (
            <div key={`timeline-${participant.account_id}-${participant.choice}`} className="flex items-start gap-3 pb-3 border-b border-gray-200">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-700 font-bold">{index + 2}</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">
                  {participant.account_id === wager.creator ? 'Creator Joined' : 'Participant Joined'}
                </h3>
                <p className="text-sm text-gray-600">
                  {participant.account_id} chose "{participant.choice}"
                </p>
                <p className="text-sm text-gray-600">Stake: {formatNearAmount(participant.stake)} NEAR</p>
                <a
                  href={`https://nearblocks.io/address/${participant.account_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 mt-1"
                >
                  View transactions
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          ))}

          {/* Wager Resolved */}
          {wager.status === 'Resolved' && (
            <div className="flex items-start gap-3 pb-3 border-b border-gray-200">
              <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-amber-700 font-bold">{participants.length + 2}</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Wager Resolved</h3>
                <p className="text-sm text-gray-600">Winner: {wager.final_outcome}</p>
                {resolvedAt && <p className="text-xs text-gray-500 mt-1">{resolvedAt}</p>}
                <a
                  href={`https://nearblocks.io/address/nearbets.near`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 mt-1"
                >
                  View contract transactions
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          )}

          {/* Winnings Auto-Transferred */}
          {wager.status === 'Resolved' && participants.some(p => p.claimed_winnings && toNearNumber(p.winnings) > 0) && (
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-700 font-bold">{participants.length + 3}</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Winnings Transferred</h3>
                {participants.filter(p => p.claimed_winnings && toNearNumber(p.winnings) > 0).map(winner => (
                  <div key={`winner-${winner.account_id}`} className="text-sm text-gray-600 mt-1">
                    {winner.account_id}: {formatNearAmount(winner.winnings)} NEAR
                    <a
                      href={`https://nearblocks.io/address/${winner.account_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 ml-2"
                    >
                      View
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="pt-3 border-t border-gray-200">
          <a
            href={`https://nearblocks.io/address/nearbets.near`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-purple-600 hover:text-purple-800 hover:underline inline-flex items-center gap-2 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            View all contract transactions on NEAR Explorer
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
};

export default WagerDetail;
